import { number2kanji } from '@geolonia/japanese-numeral';
import { isHiragana, isRomaji, toHiragana, toKatakana } from 'wanakana';
import { CandidateProvider } from 'components/Keyboard/modules/candidates';

import getMozcCandidates from '../mozcWasm';

const DIGIT_KANJI: Record<string, string> = {
  '0': '〇', '1': '一', '2': '二', '3': '三', '4': '四',
  '5': '五', '6': '六', '7': '七', '8': '八', '9': '九',
};

const numbersToKanji: CandidateProvider = async (input: string) => {
  try {
    const asKanji = number2kanji(parseInt(input, 10));
    return asKanji ? [asKanji] : [];
  } catch {
    return [];
  }
};

const romajiToKana = (input: string): { hiragana: string; katakana: string } => {
  try {
    return { hiragana: toHiragana(input), katakana: toKatakana(input) };
  } catch {
    return { hiragana: '', katakana: '' };
  }
};

const addCandidate = (candidates: string[], input: string, candidate: string) => {
  if (candidate && candidate !== input) {
    candidates.push(candidate);
  }
};

const numbersToDigitWiseKanji = (input: string): string => {
  return input.split('').map(char => DIGIT_KANJI[char]).join('');
};

const getCandidates: CandidateProvider = async (input, maxResults = 20) => {
  const candidates: string[] = [];

  if (!Number.isNaN(Number(input)) && input.trim() !== '') {
    candidates.push(input);
    candidates.push(...(await numbersToKanji(input)));
    candidates.push(numbersToDigitWiseKanji(input));
    return candidates;
  }

  // Romaji => Hiragana, Katakana (iOS order)
  if (isRomaji(input)) {
    const { hiragana, katakana } = romajiToKana(input);
    addCandidate(candidates, input, hiragana);
    addCandidate(candidates, input, katakana);
  }

  // Hiragana => Mozc conversion, then Katakana. The typed hiragana is never
  // surfaced (it is already confirmed in the input field).
  // (Katakana is never typed directly — no layout emits it — so there is no
  // katakana input branch; katakana is only ever offered as a conversion.)
  if (isHiragana(input)) {
    const mozcCandidates = await getMozcCandidates(input, maxResults);
    candidates.push(...mozcCandidates);

    const katakana = toKatakana(input);
    addCandidate(candidates, input, katakana);
  }

  // De-duplicate while preserving order and drop the typed input itself.
  const result = Array.from(new Set(candidates)).filter(candidate => candidate && candidate !== input);

  // Mixed input (e.g. kana + latin) may not match any conversion rules.
  // Return the raw composing text so the user can confirm it.
  if (input && result.length === 0) {
    return [input];
  }

  return result;
};

export default getCandidates;
